import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, Trash2, Pencil, ImagePlus, Megaphone, Sparkles, Palette, Eye, EyeOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import devApi from "../devApi";
import { API } from "../appApi";
import { Skeleton, spring } from "../bits";
import { DevScreen, DevCard, DevList, Tgl, Field, DevInput, DevTextarea, SaveBtn } from "./bits";

// ---------- Reviews ----------
export const DevReviews = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const d = t.admin.dev;
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const empty = { id: null, name: "", rating: 5, text: "", sort: 0, visible: true };

  const load = useCallback(() => {
    devApi.get("/admin/reviews").then((r) => setItems(r.data.reviews)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) return;
    try {
      const payload = { name: form.name.trim(), rating: Number(form.rating), text: form.text.trim(), sort: Number(form.sort) || 0, visible: form.visible };
      if (form.id) await devApi.put(`/admin/reviews/${form.id}`, payload);
      else await devApi.post("/admin/reviews", payload);
      toast.success(m.dev_saved);
      setForm(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const toggle = async (rev) => {
    await devApi.put(`/admin/reviews/${rev.id}`, { name: rev.name, rating: rev.rating, text: rev.text, sort: rev.sort || 0, visible: !rev.visible });
    load();
  };

  const del = async (rev) => {
    if (!window.confirm(m.dev_del_confirm)) return;
    await devApi.delete(`/admin/reviews/${rev.id}`);
    load();
  };

  return (
    <DevScreen title={m.sec_reviews} onBack={onBack} testid="dev-screen-reviews">
      {!form && (
        <motion.button whileTap={{ scale: 0.97 }} onClick={() => setForm({ ...empty })} className="mb-4 w-full h-12 rounded-2xl bg-brand text-brand-cream font-bold flex items-center justify-center gap-2" data-testid="dev-review-add">
          <Plus className="h-5 w-5" /> {d.rev_add}
        </motion.button>
      )}
      {form && (
        <DevCard className="mb-4">
          <div className="space-y-3">
            <Field label={d.rev_name}><DevInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dev-review-name" /></Field>
            <Field label={d.rev_rating}>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} className="p-1" data-testid={`dev-review-star-${n}`}>
                    <Star className={`h-6 w-6 ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-brand-ink/20"}`} />
                  </button>
                ))}
              </div>
            </Field>
            <Field label={d.rev_text}><DevTextarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="dev-review-text" /></Field>
            <div className="flex items-center gap-3">
              <SaveBtn onClick={save} label={d.rev_save} testid="dev-review-save" />
              <button type="button" onClick={() => setForm(null)} className="text-sm font-bold text-brand-ink/50 px-2" data-testid="dev-review-cancel">✕</button>
            </div>
          </div>
        </DevCard>
      )}
      {items === null ? <Skeleton className="h-20" /> : items.length === 0 ? (
        <p className="text-sm text-brand-ink/40 italic py-8 text-center">{m.dev_empty}</p>
      ) : (
        <DevList>
          {items.map((rev) => (
            <DevCard key={rev.id} className={rev.visible ? "" : "opacity-60"}>
              <div className="flex items-start gap-2" data-testid={`dev-review-${rev.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-brand-ink">{rev.name} <span className="text-amber-400">{"★".repeat(rev.rating)}</span></p>
                  <p className="text-xs text-brand-ink/60 mt-0.5 whitespace-pre-wrap">{rev.text}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button type="button" onClick={() => toggle(rev)} className="grid place-items-center h-8 w-8 rounded-full text-brand hover:bg-brand-soft/60" data-testid={`dev-review-visible-${rev.id}`} aria-label="visible">
                    {rev.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => setForm({ id: rev.id, name: rev.name, rating: rev.rating, text: rev.text, sort: rev.sort || 0, visible: rev.visible })} className="grid place-items-center h-8 w-8 rounded-full text-brand hover:bg-brand-soft/60" data-testid={`dev-review-edit-${rev.id}`} aria-label="edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => del(rev)} className="grid place-items-center h-8 w-8 rounded-full text-rose-400 hover:bg-rose-50" data-testid={`dev-review-del-${rev.id}`} aria-label="delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </DevCard>
          ))}
        </DevList>
      )}
    </DevScreen>
  );
};

// ---------- Gallery ----------
export const DevGallery = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    devApi.get("/gallery").then((r) => setItems(r.data)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await devApi.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(m.dev_saved);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const del = async (img) => {
    if (!window.confirm(m.dev_del_confirm)) return;
    await devApi.delete(`/admin/gallery/${img.id}`);
    load();
  };

  return (
    <DevScreen title={m.sec_gallery} onBack={onBack} testid="dev-screen-gallery">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="dev-gallery-file" />
      <motion.button whileTap={{ scale: 0.97 }} disabled={busy} onClick={() => fileRef.current?.click()} className="mb-4 w-full h-12 rounded-2xl bg-brand text-brand-cream font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="dev-gallery-upload">
        <ImagePlus className="h-5 w-5" /> {busy ? m.dev_uploading : m.dev_upload}
      </motion.button>
      {items === null ? <Skeleton className="h-40" /> : items.length === 0 ? (
        <p className="text-sm text-brand-ink/40 italic py-8 text-center">{m.dev_empty}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((img, i) => (
            <motion.div key={img.id} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ ...spring, delay: i * 0.05 }} className="relative rounded-2xl overflow-hidden border border-brand/10 bg-white" data-testid={`dev-gallery-img-${img.id}`}>
              <img src={`${API}/${img.url || img.storage_path}`} alt="" className="w-full aspect-square object-cover" loading="lazy" />
              <button type="button" onClick={() => del(img)} className="absolute top-2 right-2 grid place-items-center h-8 w-8 rounded-full bg-black/55 text-white" data-testid={`dev-gallery-del-${img.id}`} aria-label="delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </DevScreen>
  );
};

// ---------- Announcement ----------
export const DevAnnouncement = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ title: "", body: "" });

  const load = useCallback(() => {
    devApi.get("/admin/announcements").then((r) => setItems(r.data)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("body", form.body.trim());
      await devApi.post("/admin/announcements", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(m.dev_saved);
      setForm({ title: "", body: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  return (
    <DevScreen title={m.sec_announcement} onBack={onBack} testid="dev-screen-announcement">
      <DevCard>
        <div className="space-y-3">
          <Field label="Τίτλος"><DevInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="dev-ann-title" /></Field>
          <Field label="Κείμενο"><DevTextarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="dev-ann-body" /></Field>
          <SaveBtn onClick={save} label={m.dev_save} testid="dev-ann-save" />
        </div>
      </DevCard>
      <div className="mt-4">
        {items === null ? <Skeleton className="h-16" /> : items.length === 0 ? (
          <p className="text-sm text-brand-ink/40 italic py-6 text-center">{m.dev_empty}</p>
        ) : (
          <DevList>
            {items.map((a) => (
              <DevCard key={a.id}>
                <p className="font-bold text-sm text-brand-ink flex items-center gap-2" data-testid={`dev-ann-${a.id}`}><Megaphone className="h-4 w-4 text-brand shrink-0" /> {a.title}</p>
                <p className="text-xs text-brand-ink/60 mt-1 whitespace-pre-wrap">{a.body}</p>
              </DevCard>
            ))}
          </DevList>
        )}
      </div>
    </DevScreen>
  );
};

// ---------- Site popup ----------
export const DevPopup = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    devApi.get("/admin/popup").then((r) => setForm(r.data)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);

  const save = async () => {
    setBusy(true);
    try {
      await devApi.put("/admin/popup", form);
      toast.success(m.dev_saved);
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
    finally { setBusy(false); }
  };

  if (!form) return <DevScreen title={m.sec_popup} onBack={onBack} testid="dev-screen-popup"><Skeleton className="h-40" /></DevScreen>;

  return (
    <DevScreen title={m.sec_popup} onBack={onBack} testid="dev-screen-popup">
      <DevList>
        <DevCard>
          <div className="flex items-center justify-between">
            <p className="font-bold text-sm text-brand-ink flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand" /> {m.dev_active}</p>
            <Tgl on={!!form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} testid="dev-popup-enabled" />
          </div>
        </DevCard>
        <DevCard>
          <div className="space-y-3">
            <Field label="Τίτλος"><DevInput value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="dev-popup-title" /></Field>
            <Field label="Κείμενο"><DevTextarea value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="dev-popup-text" /></Field>
            <Field label="Κουμπί (ετικέτα)"><DevInput value={form.btn_label || ""} onChange={(e) => setForm({ ...form, btn_label: e.target.value })} data-testid="dev-popup-btn" /></Field>
            <Field label="Σύνδεσμος κουμπιού (https://…)">
              <DevInput value={form.btn_url || ""} onChange={(e) => setForm({ ...form, btn_url: e.target.value, btn_kind: e.target.value.trim() ? "url" : "none" })} data-testid="dev-popup-url" />
            </Field>
            <SaveBtn onClick={save} busy={busy} label={m.dev_save} testid="dev-popup-save" />
          </div>
        </DevCard>
      </DevList>
    </DevScreen>
  );
};

// ---------- Themes ----------
const THEMES = ["none", "christmas", "halloween", "apokries", "easter", "summer"];
const THEME_LABEL = { none: "—", christmas: "Χριστούγεννα", halloween: "Halloween", apokries: "Απόκριες", easter: "Πάσχα", summer: "Καλοκαίρι" };

export const DevThemes = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [active, setActive] = useState(null);
  const [splash, setSplash] = useState(false);

  useEffect(() => {
    devApi.get("/theme").then((r) => { setActive(r.data.active || "none"); setSplash(!!r.data.splash); }).catch(() => setActive("none"));
  }, []);

  const pick = async (th) => {
    setActive(th);
    try { await devApi.put("/admin/theme", { active: th }); } catch { toast.error(m.error_generic); }
  };

  const toggleSplash = async (v) => {
    setSplash(v);
    try { await devApi.put("/admin/theme", { active, splash: v }); } catch { toast.error(m.error_generic); }
  };

  if (active === null) return <DevScreen title={m.sec_themes} onBack={onBack} testid="dev-screen-themes"><Skeleton className="h-40" /></DevScreen>;

  return (
    <DevScreen title={m.sec_themes} onBack={onBack} testid="dev-screen-themes">
      <div className="grid grid-cols-2 gap-3">
        {THEMES.map((th, i) => (
          <motion.button
            key={th}
            type="button"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...spring, delay: i * 0.05 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => pick(th)}
            className={`rounded-2xl border p-4 text-left transition-colors ${active === th ? "border-brand bg-brand text-brand-cream shadow-[0_10px_28px_rgba(134,77,99,0.35)]" : "border-brand/10 bg-white text-brand-ink"}`}
            data-testid={`dev-theme-${th}`}
          >
            <Palette className={`h-5 w-5 mb-2 ${active === th ? "text-brand-cream" : "text-brand"}`} />
            <p className="font-bold text-sm">{THEME_LABEL[th]}</p>
          </motion.button>
        ))}
      </div>
      <DevCard className="mt-4">
        <div className="flex items-center justify-between">
          <p className="font-bold text-sm text-brand-ink">{m.dev_splash}</p>
          <Tgl on={splash} onChange={toggleSplash} testid="dev-theme-splash" />
        </div>
      </DevCard>
    </DevScreen>
  );
};
